import type { ReactNode } from "react";

type Tone = "default" | "warm" | "cool" | "playing";

interface Props {
  size?: number;
  tone?: Tone;
  showCorners?: boolean;
  children: ReactNode;
  className?: string;
}

const TONES: Record<Tone, { bg: string; ring: string }> = {
  default: {
    bg: "radial-gradient(circle at 50% 40%, rgba(240,200,74,0.14) 0%, rgba(232,68,58,0.05) 40%, rgba(8,7,10,0) 75%)",
    ring: "rgba(240,200,74,0.18)",
  },
  warm: {
    bg: "radial-gradient(circle at 50% 35%, rgba(240,200,74,0.20) 0%, rgba(232,68,58,0.08) 45%, rgba(8,7,10,0) 80%)",
    ring: "rgba(240,200,74,0.30)",
  },
  cool: {
    bg: "radial-gradient(circle at 50% 40%, rgba(90,176,200,0.18) 0%, rgba(8,7,10,0) 75%)",
    ring: "rgba(90,176,200,0.25)",
  },
  playing: {
    bg: "radial-gradient(circle at 50% 30%, rgba(232,68,58,0.30) 0%, rgba(240,200,74,0.18) 40%, rgba(8,7,10,0) 80%)",
    ring: "rgba(232,68,58,0.45)",
  },
};

export function AvatarFrame({
  size = 96,
  tone = "default",
  showCorners = false,
  children,
  className = "",
}: Props): JSX.Element {
  const t = TONES[tone];
  const pad = Math.max(8, size * 0.12);
  const total = size + pad * 2;
  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{
        width: total,
        height: total,
        background: t.bg,
        boxShadow: `inset 0 0 0 1px ${t.ring}`,
      }}
    >
      {showCorners && (
        <>
          <span
            className="absolute left-1 top-1 h-2 w-2 border-l border-t"
            style={{ borderColor: t.ring }}
          />
          <span
            className="absolute right-1 top-1 h-2 w-2 border-r border-t"
            style={{ borderColor: t.ring }}
          />
          <span
            className="absolute bottom-1 left-1 h-2 w-2 border-b border-l"
            style={{ borderColor: t.ring }}
          />
          <span
            className="absolute bottom-1 right-1 h-2 w-2 border-b border-r"
            style={{ borderColor: t.ring }}
          />
        </>
      )}
      {children}
    </div>
  );
}
